import { FormPagePropertyItemSelectionRegistrationLimitSetting } from 'backend';
import { isSelectableRegistrationLimitOption } from 'models';
import {
  SingleSelectionProperty,
  FormPageInputItem,
  FormPageInputItemSingleSelection,
  FormPageInputItemSingleSelectionRadio,
  FormPageInputItemSingleSelectionPulldown,
  INPUT_ITEM_TYPE,
  SINGLE_SELECTION_PROPERTY_INPUT_TYPE,
} from 'shared';

const handlePulldown = (
  property: SingleSelectionProperty,
  formPageInputItem?: FormPageInputItem,
): FormPageInputItemSingleSelectionPulldown => {
  const inputItemType = INPUT_ITEM_TYPE.singleSelectionPulldown;
  const { defaultSelectedValue } = property.inputFormat;

  if (
    formPageInputItem?.inputItemType === INPUT_ITEM_TYPE.singleSelectionPulldown
  ) {
    const { candidates } = property;
    // もし、inputItemのvalueがcandidatesに含まれなかった場合、
    // candidates[0].valueをつかう
    // ここを通るのは一度送信後戻ってきている場面なので、
    // そのときにdefaultValueを使うと設定した値と違う値が設定されている可能性があり不自然なため
    const { value } = formPageInputItem;
    const firstOptionValue = candidates[0].value ?? undefined;
    const valueIncludeCandidates = candidates.some(
      (candidate) => candidate.value === value,
    );
    const defaultValueCandidate = valueIncludeCandidates
      ? value
      : firstOptionValue;
    const defaultValue = getSelectableValue(
      property.registrationLimit,
      defaultValueCandidate,
    );
    return {
      inputItemType,
      value: defaultValue,
    };
  }

  const defaultValue = getSelectableValue(
    property.registrationLimit,
    defaultSelectedValue,
  );
  return {
    inputItemType,
    value: defaultValue,
  };
};

type Subtexts = FormPageInputItemSingleSelectionRadio['subtexts'];
const convertSubtexts = (
  candidates: SingleSelectionProperty['candidates'],
  inputItemSubtexts?: Subtexts,
): Subtexts => {
  return candidates
    .filter(({ value, subtext }) => value != null && subtext)
    .map((candidate) => {
      const inputItemSubtext = inputItemSubtexts?.find(
        ({ value }) => value != null && value === candidate.value,
      );
      return (
        inputItemSubtext ?? {
          value: candidate.value ?? undefined,
          subtext: '',
        }
      );
    });
};

const handleRadio = (
  property: SingleSelectionProperty,
  formPageInputItem?: FormPageInputItem,
): FormPageInputItemSingleSelectionRadio => {
  const inputItemType = INPUT_ITEM_TYPE.singleSelectionRadio;
  const { candidates } = property;
  if (
    formPageInputItem?.inputItemType === INPUT_ITEM_TYPE.singleSelectionRadio
  ) {
    const subtexts = convertSubtexts(candidates, formPageInputItem.subtexts);
    // もし、inputItemのvalueがcandidatesに含まれなかった場合はundefinedで未選択状態にする
    const { value } = formPageInputItem;
    const valueIncludeCandidates = candidates.some(
      (candidate) => candidate.value === value,
    );
    const defaultValueCandidate = valueIncludeCandidates ? value : undefined;
    const defaultValue = getSelectableValue(
      property.registrationLimit,
      defaultValueCandidate,
    );
    return {
      inputItemType,
      value: defaultValue,
      subtexts: subtexts,
    };
  }
  const { defaultSelectedValue } = property.inputFormat;
  const defaultValue = getSelectableValue(
    property.registrationLimit,
    defaultSelectedValue,
  );
  return {
    inputItemType,
    value: defaultValue,
    subtexts: convertSubtexts(candidates),
  };
};

export const convertSingleSelectionInitialValue = (
  property: SingleSelectionProperty,
  formPageInputItem?: FormPageInputItem,
): FormPageInputItemSingleSelection => {
  const { radio, radioHorizontal, pulldown } =
    SINGLE_SELECTION_PROPERTY_INPUT_TYPE;
  switch (property.inputFormat.inputType) {
    case radio:
    case radioHorizontal:
      return handleRadio(property, formPageInputItem);
    case pulldown:
      return handlePulldown(property, formPageInputItem);
  }
};

const getSelectableValue = (
  registrationLimit:
    | FormPagePropertyItemSelectionRegistrationLimitSetting
    | undefined,
  defaultSelectedValue: number | null | undefined,
) => {
  if (defaultSelectedValue == null) {
    return undefined;
  }
  if (registrationLimit == null) {
    return defaultSelectedValue ?? undefined;
  }

  const limitSetting = registrationLimit.limits.find(
    (item) => item.value === defaultSelectedValue,
  );
  if (
    isSelectableRegistrationLimitOption(
      limitSetting,
      registrationLimit?.currentTime,
    )
  ) {
    return defaultSelectedValue;
  }
  return undefined;
};
